import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { g as getAuthClient } from './index3.js';

const checkSlot = (slot, allowedSlotElements, disallowedSlotElements) => {
    const allowedElements = (allowedSlotElements === null || allowedSlotElements === void 0 ? void 0 : allowedSlotElements[slot.name]) || [];
    const disallowedElements = (disallowedSlotElements === null || disallowedSlotElements === void 0 ? void 0 : disallowedSlotElements[slot.name]) || [];
    if (allowedElements.length === 0 && disallowedElements.length === 0) {
        return;
    }
    const nodes = slot.assignedNodes();
    nodes.forEach((node) => {
        const name = node.nodeName.toLowerCase();
        if ((allowedElements.length > 0 && !allowedElements.includes(name)) || disallowedElements.includes(name)) {
            node.parentElement.removeChild(node);
            // eslint-disable-next-line no-console
            console.error(`Element <${name}> is not allowed in slot "${slot.name}". It has been removed.`);
        }
    });
};
/**
 * Limits the kind of elements that can be inserted into a (named) slot of a web component.
 * Illegal elements are removed from the DOM.
 * This function must be called in componentDidLoad method of the web component.
 * It only works with shadow DOM.
 * @param host The host element of the component.
 * @param options An object that defines the allowed element-names (in lowercase) for each slot.
 * */
const limitSlotElements = (host, options) => {
    const slots = host.shadowRoot.querySelectorAll("slot");
    slots.forEach((slot) => {
        checkSlot(slot, options.allowed, options.disallowed);
        slot.addEventListener("slotchange", () => {
            checkSlot(slot, options.allowed, options.disallowed);
        });
    });
};

const authImageCss = ":root,.ux-theme-web{--ux-account-switch-background:#eff0f4;--ux-account-switch-button-background:#fff;--ux-account-switch-button-background-hover:#005074;--ux-account-switch-button-height:40px;--ux-account-switch-button-margin:5px 0;--ux-account-switch-button-padding:10px;--ux-account-switch-button-text:#5a5a5a;--ux-account-switch-button-text-align:start;--ux-account-switch-button-text-hover:#fff;--ux-account-switch-font:var(--ux-font);--ux-account-switch-font-size:14px;--ux-account-switch-footer-background:#c3cfd6;--ux-account-switch-footer-background-hover:#005074;--ux-account-switch-footer-font-weight:700;--ux-account-switch-footer-height:44px;--ux-account-switch-footer-justify-content:center;--ux-account-switch-footer-text:#0b0b0b;--ux-account-switch-footer-text-hover:#fff;--ux-account-switch-footer-text-transform:uppercase;--ux-account-switch-list-padding:10px 20px;--ux-account-switch-text:#5a5a5a;--ux-account-switch-title-font-weight:700;--ux-account-switch-title-text-transform:uppercase;--ux-account-switch-transition:background-color 0.2s ease-in-out, color 0.2s ease-in-out}.uic-theme-responsive,.ux-theme-responsive{--ux-account-switch-background:#fff;--ux-account-switch-button-background:var(--uic-color-grey1, #e7ecf0);--ux-account-switch-button-background-hover:var(--uic-color-blue4, #285172);--ux-account-switch-button-height:40px;--ux-account-switch-button-margin:5px 0;--ux-account-switch-button-padding:10px;--ux-account-switch-button-text:var(--uic-color-black, #000);--ux-account-switch-button-text-align:start;--ux-account-switch-button-text-hover:var(--uic-color-white, #fff);--ux-account-switch-font:\"Open Sans\", sans-serif;--ux-account-switch-font-size:14px;--ux-account-switch-footer-background:var(--ux-account-switch-button-background);--ux-account-switch-footer-background-hover:var(--ux-account-switch-button-background-hover);--ux-account-switch-footer-font-weight:400;--ux-account-switch-footer-height:var(--ux-account-switch-button-height);--ux-account-switch-footer-justify-content:flex-start;--ux-account-switch-footer-text:var(--ux-account-switch-button-text);--ux-account-switch-footer-text-hover:var(--ux-account-switch-button-text-hover);--ux-account-switch-footer-text-transform:none;--ux-account-switch-list-padding:10px 0 0;--ux-account-switch-text:var(--uic-color-black, #000);--ux-account-switch-title-font-weight:400;--ux-account-switch-title-text-transform:none;--ux-account-switch-transition:background-color 0.2s ease-in-out, color 0.2s ease-in-out}:root,.uic-theme-responsive,.ux-theme-responsive{--uxc-app-header-height:52px;--uxc-app-header-expandables-y-offset:56px;--uxc-app-header-expandables-min-width:300px;--uxc-app-header-z-index:400;--uxc-app-header-bg-color:var(--uic-color-blue5);--uxc-app-header-separator-color:var(--uic-color-white);--uxc-app-header-launcher-width:68px;--uxc-app-header-breakpoint-burger-menu:680px}@media only screen and (min-width: 1024px){.uic-theme-responsive,.ux-theme-responsive{--uxc-app-header-height:68px;--uxc-app-header-expandables-y-offset:72px;--uxc-app-header-launcher-width:85px}}:root,.uic-theme-responsive{--uxc-app-toast-notifications-z-index:350}:root{--ux-flyout-bg-color:#fff;--ux-flyout-content-min-height:220px;--ux-flyout-content-padding:17px 12px 17px 12px;--ux-flyout-shadow:0 4px 4px 0 rgba(0, 0, 0, 0.05), 0 8px 8px 0 rgba(161, 161, 161, 0.4);--ux-flyout-width:296px;--ux-flyout-max-width:calc(100vw - 40px);--ux-flyout-z-index:390}@media only screen and (min-width: 769px){:root{--ux-flyout-width:320px}}@media only screen and (min-width: 1920px){:root{--ux-flyout-width:384px}}:root{--uxc-menu-item-background-color:#142939;--uxc-menu-item-background-color-hover:#2c3e4d;--uxc-menu-item-text-color:#fff;--uxc-menu-item-text-color-active:#51a7eb;--uxc-menu-item-text-color-disabled:#ccc;--uxc-menu-item-min-height:52px;--uxc-menu-item-min-width:var(--uxc-menu-item-min-height);--uxc-menu-item-font-size:1rem;--uxc-menu-item-font-weight:700;--uxc-menu-item-line-height:1.3;--uxc-menu-item-divider-color:#fff;--uxc-menu-item-dropdown-width:300px;--uxc-menu-item-dropdown-offset:4px;--uxc-menu-item-label-display:block;--uxc-menu-item-expanded-indicator-display:none}@media screen and (min-width: 1024px){:root{--uxc-menu-item-min-height:68px}}:host{display:block}";

const AuthImage = /*@__PURE__*/ proxyCustomElement(class AuthImage extends HTMLElement {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        this.errorEvent = createEvent(this, "uxError", 7);
        /** the http header for the image request */
        this.header = {};
        /** authentication scope to use */
        this.scope = null;
        /** The bearer token to use for authentication */
        this.token = null;
        this.imageElement = null;
        this.authClient = null;
        this.handleSlot = () => {
            this.imageElement = this.host.querySelector("ux-image");
            this.requestImage();
        };
        this.handleToken = (token) => {
            this.token = token;
        };
    }
    requestImage() {
        if (this.imageElement === null || !this.src || (Object.keys(this.header).length === 0 && this.token === null)) {
            return;
        }
        if (this.src === "" || this.authClient === null) {
            this.imageElement.error = true;
            return;
        }
        const headers = new Headers();
        if (this.token !== null) {
            headers.append("Authorization", `Bearer ${this.token}`);
        }
        for (const [key, value] of Object.entries(this.header)) {
            headers.append(key, value);
        }
        this.imageElement.spinner = true;
        this.imageElement.error = false;
        fetch(this.src, { headers })
            .then((response) => {
            if (response.ok) {
                return response.blob();
            }
            else {
                throw new Error("Network response was not ok.");
            }
        })
            .then((blob) => {
            const url = URL.createObjectURL(blob);
            this.imageElement.src = url;
            this.imageElement.spinner = false;
            const onReady = () => {
                URL.revokeObjectURL(url);
                this.imageElement.removeEventListener("uxReady", onReady);
            };
            this.imageElement.addEventListener("uxReady", onReady);
        })
            .catch((error) => {
            this.imageElement.error = true;
            this.errorEvent.emit({
                error,
                src: this.src
            });
        });
    }
    componentWillLoad() {
        this.authClient = getAuthClient(this.scope);
        this.token = this.authClient.getAccessToken();
    }
    componentDidLoad() {
        limitSlotElements(this.host, {
            allowed: {
                "": ["ux-image"]
            }
        });
        this.host.shadowRoot.addEventListener("slotchange", () => {
            this.handleSlot();
        });
        this.handleSlot();
    }
    connectedCallback() {
        this.authClient = getAuthClient(this.scope);
        this.authClient.subscribe(this.handleToken);
        this.token = this.authClient.getAccessToken();
    }
    disconnectedCallback() {
        var _a;
        (_a = this.authClient) === null || _a === void 0 ? void 0 : _a.unsubscribe(this.handleToken);
    }
    render() {
        return (h(Host, { key: '7c6d8e183f6afeea8d16773732db6108223e9b37' }, h("slot", { key: '6f5137df40e2b596c0674a51032a8d046f875d3c' })));
    }
    get host() { return this; }
    static get watchers() { return {
        "src": ["requestImage"],
        "header": ["requestImage"],
        "scope": ["requestImage"],
        "token": ["requestImage"]
    }; }
    static get style() { return authImageCss; }
}, [257, "uxc-auth-image", {
        "src": [1],
        "header": [16],
        "scope": [1],
        "token": [32]
    }, undefined, {
        "src": ["requestImage"],
        "header": ["requestImage"],
        "scope": ["requestImage"],
        "token": ["requestImage"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["uxc-auth-image"];
    components.forEach(tagName => { switch (tagName) {
        case "uxc-auth-image":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, AuthImage);
            }
            break;
    } });
}

const UxcAuthImage = AuthImage;
const defineCustomElement = defineCustomElement$1;

export { UxcAuthImage, defineCustomElement };
//# sourceMappingURL=uxc-auth-image.js.map

//# sourceMappingURL=uxc-auth-image.js.map